/**
 * A simple mutex/lock implementation for preventing concurrent operations.
 * Used to prevent race conditions during sync operations.
 */
export class SyncLock {
  private locked = false;
  private waitQueue: Array<() => void> = [];

  /**
   * Acquires the lock. If the lock is already held, waits until it's released.
   * @returns A promise that resolves when the lock is acquired
   */
  async acquire(): Promise<void> {
    if (!this.locked) {
      this.locked = true;
      return;
    }

    return new Promise<void>((resolve) => {
      this.waitQueue.push(resolve);
    });
  }

  /**
   * Releases the lock and allows the next waiting operation to proceed.
   */
  release(): void {
    if (this.waitQueue.length > 0) {
      const next = this.waitQueue.shift();
      next?.();
    } else {
      this.locked = false;
    }
  }

  /**
   * Executes a function with the lock acquired, automatically releasing after completion.
   * @param fn The function to execute while holding the lock
   * @returns The result of the function
   */
  async withLock<T>(fn: () => Promise<T>): Promise<T> {
    await this.acquire();
    try {
      return await fn();
    } finally {
      this.release();
    }
  }

  /**
   * Returns whether the lock is currently held.
   */
  get isLocked(): boolean {
    return this.locked;
  }
}

/**
 * A semaphore implementation for limiting concurrent operations.
 * Useful for batching DB operations.
 */
export class Semaphore {
  private permits: number;
  private waitQueue: Array<() => void> = [];

  constructor(permits: number) {
    this.permits = permits;
  }

  async acquire(): Promise<void> {
    if (this.permits > 0) {
      this.permits--;
      return;
    }

    return new Promise<void>((resolve) => {
      this.waitQueue.push(resolve);
    });
  }

  release(): void {
    if (this.waitQueue.length > 0) {
      const next = this.waitQueue.shift();
      next?.();
    } else {
      this.permits++;
    }
  }

  async withPermit<T>(fn: () => Promise<T>): Promise<T> {
    await this.acquire();
    try {
      return await fn();
    } finally {
      this.release();
    }
  }
}

